import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Content, Header, Page, Progress } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import { useSearchParams } from 'react-router-dom';
import { spassuCodeApiRef } from '../../api';
import type { ManagedProject } from '../../api/types';
import { useProjectRole } from '../../hooks/useProjectRole';
import { ProjectMembersPanel } from '../ProjectMembersPanel';
import { AuditLogPanel } from '../AuditLogPanel';
import { ComplianceDashboard } from '../ComplianceDashboard';
import { RoleBadge } from '../RoleBadge';
import { GlobalAdminsPanel } from '../GlobalAdminsPanel';

const useStyles = makeStyles(theme => ({
  selector: {
    marginBottom: theme.spacing(3),
    minWidth: 300,
  },
  roleInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  noAccess: {
    textAlign: 'center',
    marginTop: theme.spacing(4),
  },
  tabs: {
    marginBottom: theme.spacing(3),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tabPanel: {
    paddingTop: theme.spacing(1),
  },
}));

type TabKey = 'projects' | 'compliance' | 'audit-log' | 'global-admins';

const TAB_QUERY_PARAM = 'tab';

function parseTab(value: string | null): TabKey {
  if (value === 'global-admins') return 'global-admins';
  if (value === 'compliance') return 'compliance';
  if (value === 'audit-log') return 'audit-log';
  return 'projects';
}

export const ProjectManagementPage = () => {
  const classes = useStyles();
  const api = useApi(spassuCodeApiRef);
  const {
    loading: rbacLoading,
    isAdmin,
    hasPermission,
    getRolesForProject,
    refresh: refreshRoles,
    userId,
  } = useProjectRole();
  // O state guarda o ManagedProject inteiro (não só o name) para preservar o
  // providerId — dois owners com o mesmo nome em providers diferentes
  // (`spassu` em Azure e em GitHub, p.ex.) são entradas distintas no
  // dropdown e precisam ser distinguíveis ao propagar para os filhos.
  const [selectedProject, setSelectedProject] =
    useState<ManagedProject | null>(null);
  const selectedProjectName = selectedProject?.name ?? '';
  // ID composto usado como `value` do <Select> para disambiguar collisions.
  const composeProjectId = (p: ManagedProject) => `${p.providerId}:${p.name}`;
  const [refreshingCache, setRefreshingCache] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    message: string;
    severity: 'success' | 'error';
  } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = useMemo<TabKey>(
    () => parseTab(searchParams.get(TAB_QUERY_PARAM)),
    [searchParams],
  );

  const setTab = useCallback(
    (value: TabKey) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (value === 'projects') next.delete(TAB_QUERY_PARAM);
          else next.set(TAB_QUERY_PARAM, value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Reset tab to 'projects' if admin-only tab is selected without admin role
  useEffect(() => {
    if (!rbacLoading && !isAdmin && tab === 'global-admins') {
      setTab('projects');
    }
  }, [rbacLoading, isAdmin, tab, setTab]);

  const handleInvalidateCache = useCallback(async () => {
    setRefreshingCache(true);
    try {
      await api.invalidateRbacCache();
      refreshRoles();
      setSnackbar({
        severity: 'success',
        message: 'Permissões atualizadas com sucesso.',
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setSnackbar({
        severity: 'error',
        message: `Erro ao atualizar permissões: ${msg}`,
      });
    } finally {
      setRefreshingCache(false);
    }
  }, [api, refreshRoles]);

  // Fetch projects agnosticamente: agrega owners de todos os providers SCM
  // registrados (Azure, GitHub, etc.). Cada owner equivale a um project_key
  // no esquema RBAC agnóstico (project_key=repoOwner). Substitui o antigo
  // api.listProjects() Azure-only que deixava a página vazia em deploys
  // multi-provider sem Azure configurado.
  const { value: managedProjects, loading: projectsLoading } = useAsync(
    () => api.listManagedProjects(),
    [],
  );

  const loading = rbacLoading || projectsLoading;

  // Para admins: todos os projetos descobertos. Para os demais: filtra pelos
  // permissions do usuário no project_key (= owner) correspondente.
  const availableProjects = isAdmin
    ? managedProjects ?? []
    : (managedProjects ?? []).filter(
        p =>
          hasPermission(p.name, 'member.list') ||
          hasPermission(p.name, 'member.add') ||
          hasPermission(p.name, 'audit.view'),
      );

  // Projetos nos quais o usuario pode consultar audit log
  const auditableProjects = isAdmin
    ? managedProjects ?? []
    : (managedProjects ?? []).filter(p => hasPermission(p.name, 'audit.view'));
  const canSeeAuditTab = isAdmin || auditableProjects.length > 0;

  // Reset tab to 'projects' if audit-log is selected but caller has no audit.view anywhere
  useEffect(() => {
    if (!rbacLoading && tab === 'audit-log' && !canSeeAuditTab) {
      setTab('projects');
    }
  }, [rbacLoading, tab, canSeeAuditTab, setTab]);

  if (loading) {
    return (
      <Page themeId="tool">
        <Header
          title="Gestão de Projetos"
          subtitle="Membros, Compliance e Políticas"
        />
        <Content>
          <Progress />
        </Content>
      </Page>
    );
  }

  if (availableProjects.length === 0 && !isAdmin) {
    return (
      <Page themeId="tool">
        <Header
          title="Gestão de Projetos"
          subtitle="Membros, Compliance e Políticas"
        />
        <Content>
          <div className={classes.noAccess}>
            <Typography variant="h6" color="textSecondary">
              Você não tem permissão para gerenciar membros de projetos.
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Solicite ao gestor ou administrador.
            </Typography>
            {userId && (
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ marginTop: 16, display: 'block' }}
              >
                Seu entity ref: <code>{userId}</code>
              </Typography>
            )}
          </div>
        </Content>
      </Page>
    );
  }

  const selectedProviderId = selectedProject?.providerId;

  const selectedRoles = selectedProjectName
    ? getRolesForProject(selectedProjectName)
    : [];

  return (
    <Page themeId="tool">
      <Header
        title="Gestão de Projetos"
        subtitle="Membros, Compliance e Políticas"
      >
        {isAdmin && (
          <Tooltip title="Atualizar permissões (limpar cache RBAC)">
            <IconButton
              color="inherit"
              onClick={handleInvalidateCache}
              disabled={refreshingCache}
              aria-label="Atualizar permissões"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        )}
      </Header>
      <Content>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v as TabKey)}
          indicatorColor="secondary"
          textColor="secondary"
          className={classes.tabs}
        >
          <Tab value="projects" label="Projetos" />
          {isAdmin && (
            <Tab value="global-admins" label="Administradores Globais" />
          )}
          <Tab value="compliance" label="Compliance" />
          {canSeeAuditTab && <Tab value="audit-log" label="Audit Log" />}
        </Tabs>

        {tab === 'global-admins' && isAdmin && (
          <div className={classes.tabPanel}>
            <GlobalAdminsPanel />
          </div>
        )}
        {tab === 'audit-log' && canSeeAuditTab && (
          <div className={classes.tabPanel}>
            {isAdmin ? (
              <AuditLogPanel />
            ) : (
              <>
                <FormControl
                  className={classes.selector}
                  variant="outlined"
                  size="small"
                >
                  <InputLabel>Projeto</InputLabel>
                  <Select
                    value={
                      selectedProject ? composeProjectId(selectedProject) : ''
                    }
                    onChange={e => {
                      const id = e.target.value as string;
                      setSelectedProject(
                        auditableProjects.find(
                          p => composeProjectId(p) === id,
                        ) ?? null,
                      );
                    }}
                    label="Projeto"
                  >
                    {auditableProjects.map(p => (
                      <MenuItem
                        key={composeProjectId(p)}
                        value={composeProjectId(p)}
                      >
                        {p.name}
                        <Typography
                          component="span"
                          variant="caption"
                          color="textSecondary"
                          style={{ marginLeft: 8 }}
                        >
                          ({p.providerLabel})
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {selectedProjectName &&
                hasPermission(selectedProjectName, 'audit.view') ? (
                  <AuditLogPanel projectKey={selectedProjectName} />
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    Selecione um projeto para visualizar o audit log.
                  </Typography>
                )}
              </>
            )}
          </div>
        )}
        {!(tab === 'global-admins' && isAdmin) &&
          !(tab === 'audit-log' && canSeeAuditTab) && (
          <div className={classes.tabPanel}>
            <FormControl
              className={classes.selector}
              variant="outlined"
              size="small"
            >
              <InputLabel>Projeto</InputLabel>
              <Select
                value={
                  selectedProject ? composeProjectId(selectedProject) : ''
                }
                onChange={e => {
                  const id = e.target.value as string;
                  setSelectedProject(
                    availableProjects.find(p => composeProjectId(p) === id) ??
                      null,
                  );
                }}
                label="Projeto"
              >
                {availableProjects.map(p => (
                  <MenuItem
                    key={composeProjectId(p)}
                    value={composeProjectId(p)}
                  >
                    {p.name}
                    <Typography
                      component="span"
                      variant="caption"
                      color="textSecondary"
                      style={{ marginLeft: 8 }}
                    >
                      ({p.providerLabel})
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedProject ? (
              <>
                {tab === 'projects' && (
                  <>
                    {selectedRoles.length > 0 && (
                      <div className={classes.roleInfo}>
                        <Typography variant="body2" color="textSecondary">
                          Seus roles neste projeto:
                        </Typography>
                        {selectedRoles.map(r => (
                          <RoleBadge key={r} role={r} />
                        ))}
                      </div>
                    )}
                    <ProjectMembersPanel
                      projectKey={selectedProjectName}
                      providerId={selectedProviderId}
                    />
                  </>
                )}

                {tab === 'compliance' &&
                  (isAdmin ||
                  hasPermission(selectedProjectName, 'audit.view') ? (
                    <ComplianceDashboard projectKey={selectedProjectName} />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Você não tem permissão para visualizar Compliance neste
                      projeto.
                    </Typography>
                  ))}
              </>
            ) : (
              availableProjects.length > 0 && (
                <Typography variant="body2" color="textSecondary">
                  Selecione um projeto para continuar.
                </Typography>
              )
            )}
          </div>
        )}
      </Content>
      <Snackbar
        open={snackbar !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert
            onClose={() => setSnackbar(null)}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Page>
  );
};
